import { createFileRoute, notFound } from '@tanstack/react-router'
import CourseDetailsPage from '#/pages/CourseDetailsPage'
import NotFoundPage from '#/pages/NotFoundPage'
import PageLoader from '#/components/common/PageLoader'
import { courseDetailQueryOptions } from '#/features/courses/hooks/useCourse'
import { coursesQueryOptions } from '#/features/courses/hooks/useCourses'

export const Route = createFileRoute('/_home/courses/$courseId')({
  pendingMs: 0,
  pendingComponent: () => (
    <PageLoader
      title="Loading Course..."
      subtitle="Fetching class modules, curriculum, and instructor details"
    />
  ),
  loader: async ({ context: { queryClient }, params }) => {
    if (!params.courseId) {
      throw notFound()
    }

    try {
      const course = await queryClient.ensureQueryData(
        courseDetailQueryOptions(params.courseId),
      )
      queryClient.prefetchQuery(coursesQueryOptions({ limit: 5 }))
      return course
    } catch (err: any) {
      // Only treat a genuine 404 response as "not found"
      // Network errors, timeouts (408), 500s → re-throw → hits errorComponent
      if (err?.status === 404) throw notFound()
      throw err
    }
  },
  notFoundComponent: () => <NotFoundPage type="course" />,
  head: ({ loaderData, params }) => {
    const title =
      loaderData?.metaTitle ||
      loaderData?.title ||
      (params.courseId ? params.courseId.replace(/-/g, ' ') : 'Course Details')
    const description =
      loaderData?.metaDescription ||
      loaderData?.shortDescription ||
      loaderData?.curriculumDescription ||
      "Explore course modules, curriculum, and Ijaza-certified instructor details at Wa'allamah Academy."
    const image = loaderData?.image

    return {
      meta: [
        {
          title: `${title} | Wa'allamah`,
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:title',
          content: `${title} | Wa'allamah`,
        },
        {
          property: 'og:description',
          content: description,
        },
        ...(image ? [{ property: 'og:image', content: image }] : []),
        {
          name: 'twitter:title',
          content: `${title} | Wa'allamah`,
        },
        {
          name: 'twitter:description',
          content: description,
        },
      ],
    }
  },
  component: CourseDetailsPage,
})
